import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CartState, CartItem, OrderType, DeliveryAddress } from '@/types'

const initialState: CartState = {
  items: [],
  total_items: 0,
  subtotal: 0,
  tax_amount: 0,
  total_amount: 0,
  discount_amount: 0,
  discount_code: undefined,
  table_id: undefined,
  order_type: 'takeaway',
  customer_id: undefined,
  delivery_address: undefined,
  notes: undefined,
  payment_method: undefined,
  isLoading: false,
  error: null,
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    addItemLocally: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(item => item.product_id === action.payload.product_id)
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity
        existingItem.total_price = existingItem.quantity * existingItem.price
      } else {
        state.items.push(action.payload)
      }
      
      state.total_items = state.items.reduce((sum, item) => sum + item.quantity, 0)
      state.subtotal = state.items.reduce((sum, item) => sum + item.total_price, 0)
      state.tax_amount = state.subtotal * 0.15 // Assuming 15% tax rate
      state.total_amount = state.subtotal + state.tax_amount - state.discount_amount
    },
    updateItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.items.find(item => item.id === action.payload.id)
      if (item) {
        item.quantity = action.payload.quantity
        item.total_price = item.quantity * item.price
        
        state.total_items = state.items.reduce((sum, item) => sum + item.quantity, 0)
        state.subtotal = state.items.reduce((sum, item) => sum + item.total_price, 0)
        state.tax_amount = state.subtotal * 0.15
        state.total_amount = state.subtotal + state.tax_amount - state.discount_amount
      }
    },
    removeItemLocally: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload)
      state.total_items = state.items.reduce((sum, item) => sum + item.quantity, 0)
      state.subtotal = state.items.reduce((sum, item) => sum + item.total_price, 0)
      state.tax_amount = state.subtotal * 0.15
      state.total_amount = state.subtotal + state.tax_amount - state.discount_amount
    },
    clearCartLocally: (state) => {
      state.items = []
      state.total_items = 0
      state.subtotal = 0
      state.tax_amount = 0
      state.total_amount = 0
      state.discount_amount = 0
      state.discount_code = undefined
      state.table_id = undefined
      state.customer_id = undefined
      state.delivery_address = undefined
      state.address_id = undefined
      state.notes = undefined
      state.payment_method = undefined
    },
    setOrderTypeLocally: (state, action: PayloadAction<OrderType>) => {
      state.order_type = action.payload
    },
    setTableLocally: (state, action: PayloadAction<string | number>) => {
      state.table_id = action.payload
    },
    setCustomerLocally: (state, action: PayloadAction<string | number>) => {
      state.customer_id = action.payload
    },
    setDeliveryAddressLocally: (state, action: PayloadAction<DeliveryAddress | undefined>) => {
      state.delivery_address = action.payload
      if (action.payload?.id) {
        state.address_id = action.payload.id
      }
    },
    setAddressIdLocally: (state, action: PayloadAction<string | number | undefined>) => {
      state.address_id = action.payload
      if (action.payload == null || action.payload === '') {
        state.delivery_address = undefined
      }
    },
    clearDeliveryAddressLocally: (state) => {
      state.address_id = undefined
      state.delivery_address = undefined
      try {
        localStorage.removeItem('selected_address_id')
      } catch {
        // ignore
      }
    },
    setNotes: (state, action: PayloadAction<string>) => {
      state.notes = action.payload
    },
    setPaymentMethod: (state, action: PayloadAction<'cash' | 'card' | 'online' | 'partial'>) => {
      state.payment_method = action.payload
    },
    updateCartFromAPI: (state, action: PayloadAction<any>) => {
      const apiData = action.payload
      
      // Transform API response to CartState format
      const transformedState: CartState = {
        items: apiData.items || [],
        total_items: apiData.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0,
        subtotal: parseFloat(apiData.sub_total || apiData.subtotal || '0'),
        tax_amount: parseFloat(apiData.tax_value || apiData.tax_amount || '0'),
        total_amount: parseFloat(apiData.total_price || apiData.total_amount || '0'),
        discount_amount: parseFloat(apiData.discount || apiData.discount_amount || '0'),
        discount_code: apiData.discount_code,
        table_id: apiData.table_id,
        order_type: state.order_type, // Keep existing order_type
        customer_id: apiData.user_id || state.customer_id,
        delivery_address: state.delivery_address, // Keep existing delivery_address
        notes: state.notes, // Keep existing notes
        isLoading: false,
        error: null,
        // API response fields
        id: apiData.id,
        user_id: apiData.user_id,
        status: apiData.status,
        branch_id: apiData.branch_id,
        platform: apiData.platform,
        address_id: apiData.address_id,
        type_id: apiData.type_id,
        tax_percentage: apiData.tax_percentage,
        delivery_price: apiData.delivery_price,
        table_price: apiData.table_price,
        price: apiData.price,
        tax_value: apiData.tax_value,
        sub_total: apiData.sub_total,
        total_price: apiData.total_price,
        discount: apiData.discount,
        allowed_payment_methods: apiData.allowed_payment_methods,
      }
      
      return transformedState
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
  },
})

export const {
  clearError,
  addItemLocally,
  updateItemQuantity,
  removeItemLocally,
  clearCartLocally,
  setOrderTypeLocally,
  setTableLocally,
  setCustomerLocally,
  setDeliveryAddressLocally,
  setAddressIdLocally,
  clearDeliveryAddressLocally,
  setNotes,
  setPaymentMethod,
  updateCartFromAPI,
  setLoading,
  setError,
} = cartSlice.actions

// Export aliases for compatibility
export const addItemToCart = addItemLocally
export const clearCart = clearCartLocally

export default cartSlice.reducer